import React from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Select,
  DatePicker,
  Checkbox,
  Button,
  Typography,
} from "antd";
import {
  FileTextOutlined,
  CalendarOutlined,
  DownOutlined,
} from "@ant-design/icons";

const { Title } = Typography;
const { Option } = Select;

interface ReportField {
  key: string;
  label: string;
}

const Reports: React.FC = () => {
  const [form] = Form.useForm();

  const reportFields: ReportField[] = [
    { key: "customerIncentiveCode", label: "Customer Incentive Code" },
    { key: "eBikeType", label: "E-Bike Type" },
    { key: "salePriceOfEBike", label: "Sale Price of E-Bike" },
    { key: "status", label: "Status" },
    { key: "preferredPaymentType", label: "Preferred Payment Type" },
    { key: "dateFunded", label: "Date Funded" },
    { key: "dateApprovedDeclined", label: "Date Approved/Declined" },
    { key: "dateOfSale", label: "Date of Sale" },
    { key: "approvedDeclinedBy", label: "Approved/Declined By" },
    { key: "serialNumber", label: "Serial Number" },
    { key: "dateShipped", label: "Date Shipped" },
    { key: "dateSubmitted", label: "Date Submitted" },
  ];

  const handleDownloadReport = () => {
    console.log("Download report clicked");
    // Implement download functionality here
  };

  const handleSelectAllChange = (checked: boolean) => {
    const allFields = reportFields.map((field) => field.key);
    form.setFieldsValue({
      reportFields: checked ? allFields : [],
    });
  };

  const handleFieldChange = (checkedValues: string[]) => {
    const isAllSelected = checkedValues.length === reportFields.length;
    form.setFieldsValue({
      selectAll: isAllSelected,
    });
  };

  return (
    <div
      style={{
        backgroundColor: "#FAFAFA",
        flex: 1,
        minHeight: "calc(100vh - 80px)",
      }}
    >
      {/* Page Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginTop: "40px",
          marginLeft: "24px",
          marginBottom: "30px",
        }}
      >
        <FileTextOutlined
          style={{
            fontSize: "27px",
            color: "#4D4D4D",
            width: "36px",
            height: "36px",
          }}
        />
        <Title
          level={2}
          style={{
            margin: 0,
            color: "#4D4D4D",
            fontFamily:
              "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
            fontSize: "24px",
            fontWeight: "700",
            lineHeight: "24px",
            letterSpacing: "0.5px",
          }}
        >
          Reports
        </Title>
      </div>

      {/* Reports Card */}
      <div style={{ margin: "0 24px" }}>
        <Card
          style={{
            borderRadius: "10px",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.20)",
            border: "none",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: "20px" }}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              reportFields: [],
              selectAll: false,
            }}
          >
            <Row gutter={[0, 0]}>
              {/* Left Column - Filters */}
              <Col span={6}>
                <div style={{ paddingRight: "20px" }}>
                  {/* Request Status */}
                  <Form.Item
                    label={
                      <span
                        style={{
                          color: "#4D4D4D",
                          fontFamily:
                            "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                          fontSize: "16px",
                          fontWeight: "400",
                          lineHeight: "16px",
                        }}
                      >
                        Request Status
                      </span>
                    }
                    name="requestStatus"
                    style={{ marginBottom: "28px" }}
                  >
                    <Select
                      placeholder="Select a status"
                      style={{
                        fontFamily:
                          "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                        fontSize: "16px",
                      }}
                      suffixIcon={
                        <DownOutlined
                          style={{ color: "#4D4D4D", fontSize: "10px" }}
                        />
                      }
                    >
                      <Option value="pending">Pending</Option>
                      <Option value="approved">Approved</Option>
                      <Option value="declined">Declined</Option>
                      <Option value="funded">Funded</Option>
                    </Select>
                  </Form.Item>

                  {/* Start Date */}
                  <Form.Item
                    label={
                      <span
                        style={{
                          color: "#4D4D4D",
                          fontFamily:
                            "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                          fontSize: "16px",
                          fontWeight: "400",
                          lineHeight: "16px",
                        }}
                      >
                        Start Date
                      </span>
                    }
                    name="startDate"
                    style={{ marginBottom: "28px" }}
                  >
                    <DatePicker
                      placeholder="MM/DD/YYYY"
                      style={{
                        width: "100%",
                        fontFamily:
                          "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                        fontSize: "16px",
                      }}
                      suffixIcon={
                        <CalendarOutlined style={{ color: "#CCC" }} />
                      }
                    />
                  </Form.Item>

                  {/* End Date */}
                  <Form.Item
                    label={
                      <span
                        style={{
                          color: "#4D4D4D",
                          fontFamily:
                            "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                          fontSize: "16px",
                          fontWeight: "400",
                          lineHeight: "16px",
                        }}
                      >
                        End Date
                      </span>
                    }
                    name="endDate"
                  >
                    <DatePicker
                      placeholder="MM/DD/YYYY"
                      style={{
                        width: "100%",
                        fontFamily:
                          "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                        fontSize: "16px",
                      }}
                      suffixIcon={
                        <CalendarOutlined style={{ color: "#CCC" }} />
                      }
                    />
                  </Form.Item>
                </div>
              </Col>

              {/* Vertical Divider */}
              <Col span={1}>
                <div
                  style={{
                    width: "1px",
                    backgroundColor: "#CCC",
                    height: "400px",
                    margin: "0 auto",
                  }}
                />
              </Col>

              {/* Right Column - Report Fields */}
              <Col span={17}>
                <div style={{ paddingLeft: "20px" }}>
                  {/* Report Title and Download Button */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "20px",
                      paddingBottom: "20px",
                      borderBottom: "1px solid #CCC",
                    }}
                  >
                    <Title
                      level={3}
                      style={{
                        margin: 0,
                        color: "#4D4D4D",
                        fontFamily:
                          "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                        fontSize: "20px",
                        fontWeight: "600",
                        lineHeight: "20px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Bike Shop Reimbursements Report
                    </Title>
                    <Button
                      type="primary"
                      onClick={handleDownloadReport}
                      style={{
                        backgroundColor: "#DF701F",
                        borderColor: "#DF701F",
                        borderRadius: "6px",
                        padding: "10px 20px",
                        height: "36px",
                        width: "151px",
                        fontFamily:
                          "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                        fontSize: "16px",
                        fontWeight: "400",
                        lineHeight: "16px",
                      }}
                    >
                      Download Report
                    </Button>
                  </div>

                  {/* Select All Checkbox */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "10px 0",
                      height: "50px",
                      marginBottom: "10px",
                    }}
                  >
                    <Form.Item
                      name="selectAll"
                      valuePropName="checked"
                      style={{ margin: 0 }}
                    >
                      <Checkbox
                        onChange={(e) =>
                          handleSelectAllChange(e.target.checked)
                        }
                        style={{
                          color: "#4D4D4D",
                          fontFamily:
                            "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                          fontSize: "16px",
                          fontWeight: "400",
                          lineHeight: "20px",
                        }}
                      >
                        Select All
                      </Checkbox>
                    </Form.Item>
                  </div>

                  {/* Report Fields Checkboxes */}
                  <Form.Item name="reportFields">
                    <Checkbox.Group
                      style={{ width: "100%" }}
                      onChange={handleFieldChange}
                    >
                      <Row gutter={[24, 0]}>
                        {/* First Column */}
                        <Col span={8}>
                          {reportFields.slice(0, 5).map((field, index) => (
                            <div
                              key={field.key}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "10px 0",
                                height: "50px",
                              }}
                            >
                              <Checkbox
                                value={field.key}
                                style={{
                                  color: "#4D4D4D",
                                  fontFamily:
                                    "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                                  fontSize: "16px",
                                  fontWeight: "400",
                                  lineHeight: "20px",
                                }}
                              >
                                {field.label}
                              </Checkbox>
                            </div>
                          ))}
                        </Col>

                        {/* Second Column */}
                        <Col span={8}>
                          {reportFields.slice(5, 8).map((field, index) => (
                            <div
                              key={field.key}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "10px 0",
                                height: "50px",
                              }}
                            >
                              <Checkbox
                                value={field.key}
                                style={{
                                  color: "#4D4D4D",
                                  fontFamily:
                                    "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                                  fontSize: "16px",
                                  fontWeight: "400",
                                  lineHeight: "20px",
                                }}
                              >
                                {field.label}
                              </Checkbox>
                            </div>
                          ))}
                        </Col>

                        {/* Third Column */}
                        <Col span={8}>
                          {reportFields.slice(8).map((field, index) => (
                            <div
                              key={field.key}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "10px 0",
                                height: "50px",
                              }}
                            >
                              <Checkbox
                                value={field.key}
                                style={{
                                  color: "#4D4D4D",
                                  fontFamily:
                                    "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
                                  fontSize: "16px",
                                  fontWeight: "400",
                                  lineHeight: "20px",
                                }}
                              >
                                {field.label}
                              </Checkbox>
                            </div>
                          ))}
                        </Col>
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                </div>
              </Col>
            </Row>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default Reports;
